import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FiX, FiUser, FiDownload, FiClock, FiCalendar, FiAlertCircle, FiRefreshCw,
} from "react-icons/fi";
import toast from "react-hot-toast";
import {
  fetchParticipantHistory,
  downloadParticipantHistoryCsv,
} from "../../../utils/eventApi";

/**
 * Where one participant's points came from, and when.
 *
 * Two tabs, because the backend records it at two resolutions and conflating them
 * would misrepresent both:
 *
 *   Timeline — every source, per UTC day. Complete, and the only record of mining,
 *              streak and referral. This is the default.
 *   Awards   — the discrete awards to the second: check-ins, Creator Hub
 *              milestones, admin adjustments, prizes.
 *
 * Mining has no per-award row and never will: it is credited on every coin-update
 * poll, which at a million users would be billions of rows on the hottest write
 * path in the app. The note under the Timeline says so, because without it an
 * admin reads the day view as imprecise rather than as designed.
 *
 * Read-only. Corrections are made with Adjust Points, which is already audited.
 */

const getUserId = (row) => row?.user_id ?? row?.userId ?? row?.id ?? null;

const SOURCE_LABEL = {
  daily_check_in: "Check-in",
  creator_hub: "Creator Hub",
  admin_adjustment: "Admin adjustment",
  prize_px: "Prize",
  task: "Task",
  tap_game: "Tap Game",
  boom_cards: "Boom Cards",
  mini_game: "Game",
  referral: "Referral join",
  exchange: "Exchange",
  exchange_refund: "Exchange refund",
};

/** Each bucket's colour, shared by the bars and the legend so they always agree. */
const BUCKETS = [
  { key: "daily_mining", label: "Mining", bar: "bg-amber-400", dot: "bg-amber-400" },
  { key: "daily_check_in", label: "Check-in", bar: "bg-indigo-500", dot: "bg-indigo-500" },
  { key: "referral", label: "Referral", bar: "bg-sky-400", dot: "bg-sky-400" },
  { key: "streak_bonus", label: "Streak", bar: "bg-teal-400", dot: "bg-teal-400" },
  { key: "creator_hub", label: "Creator Hub", bar: "bg-violet-500", dot: "bg-violet-500" },
  { key: "mini_game", label: "Games", bar: "bg-emerald-500", dot: "bg-emerald-500" },
  { key: "task", label: "Task", bar: "bg-blue-500", dot: "bg-blue-500" },
  { key: "other", label: "Adjustments", bar: "bg-rose-500", dot: "bg-rose-500" },
];

const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const fmtDay = (iso) => {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d} ${months[Number(m) - 1]}`;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

/**
 * Fill the gaps between earning days.
 *
 * A day nobody earned on writes no row, so the raw array jumps straight from the
 * 15th to the 18th. Rendering that as three adjacent bars would misread as three
 * consecutive earning days. "Nothing happened on the 16th" is an answer, and it
 * only shows if the gap is drawn.
 */
function withGaps(daily) {
  if (!daily || daily.length === 0) return [];

  const out = [];
  const oneDay = 86400000;
  let cursor = new Date(`${daily[0].day}T00:00:00Z`).getTime();
  const last = new Date(`${daily[daily.length - 1].day}T00:00:00Z`).getTime();
  let i = 0;
  let running = 0;

  while (cursor <= last && out.length < 400) {
    const iso = new Date(cursor).toISOString().slice(0, 10);
    if (i < daily.length && daily[i].day === iso) {
      running = daily[i].running_total;
      out.push({ ...daily[i], empty: false });
      i += 1;
    } else {
      out.push({ day: iso, day_total: 0, running_total: running, empty: true });
    }
    cursor += oneDay;
  }
  return out;
}

const ParticipantHistoryDrawer = ({ isOpen, onClose, eventId, participant }) => {
  const [tab, setTab] = useState("timeline");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState("");
  const [more, setMore] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const userId = getUserId(participant);

  const load = useCallback(async () => {
    if (!eventId || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchParticipantHistory(eventId, userId, { source });
      setData(res.data);
      setMore([]);
      setCursor(res.data?.ledger?.next_cursor || null);
    } catch (err) {
      setError(err.message || "Could not load this history");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [eventId, userId, source]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  useEffect(() => {
    if (isOpen) {
      setTab("timeline");
      setSource("");
    }
  }, [isOpen, userId]);

  // Escape closes, which is what a drawer is expected to do.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchParticipantHistory(eventId, userId, { source, cursor });
      setMore((prev) => [...prev, ...(res.data?.ledger?.data || [])]);
      setCursor(res.data?.ledger?.next_cursor || null);
    } catch (err) {
      toast.error(err.message || "Could not load more awards");
    } finally {
      setLoadingMore(false);
    }
  };

  const exportCsv = async () => {
    try {
      await downloadParticipantHistoryCsv(eventId, userId, data?.user?.username);
      toast.success("Export started");
    } catch (err) {
      toast.error(err.message || "Could not export this history");
    }
  };

  const days = useMemo(() => withGaps(data?.daily || []), [data]);
  const peak = useMemo(
    () => Math.max(1, ...days.map((d) => Math.abs(d.day_total || 0))),
    [days]
  );
  const awards = useMemo(
    () => [...(data?.ledger?.data || []), ...more],
    [data, more]
  );

  if (!isOpen || !participant) return null;

  const breakdown = data?.totals?.breakdown || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-3xl rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <FiUser size={16} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">
                {data?.user?.username || participant.username || "Participant"} · Points history
              </h2>
              <p className="text-xs text-gray-400">
                {data?.user?.country ? `${data.user.country} · ` : ""}
                {data?.user?.rank ? `rank #${data.user.rank}` : "unranked"}
                {data?.totals?.frozen ? " · final standings" : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={exportCsv}
              disabled={loading || !data}
              title="Export as CSV"
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition disabled:opacity-40"
            >
              <FiDownload size={16} />
            </button>
            <button
              onClick={load}
              disabled={loading}
              title="Reload"
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition disabled:opacity-40"
            >
              <FiRefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
            >
              <FiX size={17} />
            </button>
          </div>
        </div>

        {/* ── Totals ─────────────────────────────────────────────────────── */}
        {data && (
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                Total event points
              </span>
              <span className="text-lg font-bold text-gray-800 tabular-nums">
                {fmt(data.totals.total_points)}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {BUCKETS.map((b) => (
                <span key={b.key} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <span className={`w-2 h-2 rounded-full ${b.dot}`} />
                  {b.label}
                  <b className="text-gray-800 tabular-nums font-semibold">
                    {fmt(breakdown[b.key])}
                  </b>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Tabs + filter ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 px-6 py-2.5 border-b border-gray-100">
          <div className="flex gap-1">
            {[
              ["timeline", "Timeline", FiCalendar],
              ["awards", "Awards", FiClock],
            ].map(([id, label, Icon]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition ${
                  tab === id
                    ? "bg-indigo-50 text-indigo-700"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>

          {tab === "awards" && (
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 bg-white"
            >
              <option value="">All sources</option>
              {Object.entries(SOURCE_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          )}
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <p className="p-8 text-center text-xs text-gray-400">Loading history…</p>
          )}

          {!loading && error && (
            <div className="p-8 text-center">
              <FiAlertCircle className="mx-auto mb-2 text-rose-400" size={22} />
              <p className="text-xs text-gray-600">{error}</p>
              <button
                onClick={load}
                className="mt-3 text-xs text-indigo-600 hover:underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Timeline */}
          {!loading && !error && data && tab === "timeline" && (
            <div className="p-6 pt-4">
              {days.length === 0 ? (
                <p className="py-10 text-center text-xs text-gray-400">
                  No points earned on any day yet.
                </p>
              ) : (
                <>
                  {/* Stacked bars, one per day, gaps included */}
                  <div className="flex items-end gap-[3px] h-24 mb-5 overflow-x-auto pb-1">
                    {days.map((d) => (
                      <div
                        key={d.day}
                        title={`${fmtDay(d.day)} · ${fmt(d.day_total)}`}
                        className="flex-none w-2.5 flex flex-col-reverse justify-start h-full"
                      >
                        {d.empty ? (
                          <div className="h-[2px] bg-gray-200 rounded-sm" />
                        ) : (
                          BUCKETS.map((b) => {
                            const v = Math.abs(Number(d[b.key] || 0));
                            if (!v) return null;
                            return (
                              <div
                                key={b.key}
                                className={`${b.bar} rounded-[1px]`}
                                style={{ height: `${Math.max(2, (v / peak) * 92)}%` }}
                              />
                            );
                          })
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Day list */}
                  <div className="border-t border-gray-100">
                    {days.map((d) => (
                      <div
                        key={d.day}
                        className="flex items-center gap-4 py-2 border-b border-gray-50 text-xs"
                      >
                        <span className="w-14 flex-none text-gray-400">{fmtDay(d.day)}</span>
                        <span
                          className={`w-20 flex-none text-right tabular-nums font-semibold ${
                            d.empty ? "text-gray-300" : "text-gray-800"
                          }`}
                        >
                          {d.empty ? "—" : `+${fmt(d.day_total)}`}
                        </span>
                        <span className="flex-1 text-gray-500 truncate">
                          {d.empty
                            ? "no activity"
                            : BUCKETS.filter((b) => Number(d[b.key] || 0) !== 0)
                                .map((b) => `${b.label} ${fmt(d[b.key])}`)
                                .join(" · ")}
                        </span>
                        <span className="w-24 flex-none text-right tabular-nums text-gray-400">
                          {fmt(d.running_total)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <p className="mt-4 text-[11px] leading-relaxed text-gray-400">
                    Mining, streak and referral points are recorded per day. Check-ins,
                    Creator Hub milestones and admin adjustments have exact times — see
                    the Awards tab.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Awards */}
          {!loading && !error && data && tab === "awards" && (
            <div className="p-6 pt-4">
              {awards.length === 0 ? (
                <p className="py-10 text-center text-xs text-gray-400">
                  No individual awards{source ? " of this kind" : ""} for this user.
                  {!source && " Their points came from mining, which is recorded per day."}
                </p>
              ) : (
                <>
                  <div className="border-t border-gray-100">
                    {awards.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-start gap-4 py-2.5 border-b border-gray-50 text-xs"
                      >
                        <span className="w-32 flex-none text-gray-400">{fmtTime(a.at)}</span>
                        <span
                          className={`w-20 flex-none text-right tabular-nums font-semibold ${
                            Number(a.points) < 0 ? "text-rose-600" : "text-gray-800"
                          }`}
                        >
                          {Number(a.points) > 0 ? "+" : ""}
                          {fmt(a.points)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-700">
                            {SOURCE_LABEL[a.source] || a.source}
                            {a.ref_type && (
                              <span className="text-gray-400"> · {a.ref_label ? a.ref_label : `${a.ref_type} #${a.ref_id}`}</span>
                            )}
                          </p>
                          {a.note && (
                            <p className="text-[11px] text-gray-500 mt-0.5">{a.note}</p>
                          )}
                          {a.admin && (
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              by {a.admin.name || `admin #${a.admin.id}`}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {cursor && (
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="mt-4 w-full py-2 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
                    >
                      {loadingMore ? "Loading…" : "Load more"}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParticipantHistoryDrawer;
